import { constructCdnUrl } from './cloudflare-loader'

/**
 * Default placeholder images for different contexts
 */
export const DEFAULT_PLACEHOLDERS = {
  USER: '/visuals/icons/default-user.svg',
  LOCATION: '/visuals/icons/location-pin.svg',
  GENERAL: '/visuals/icons/location-pin.svg', // Default fallback for general images
}

/**
 * Type for the image context to determine which placeholder to use
 */
export type ImageContext = 'USER' | 'LOCATION' | 'GENERAL'

/**
 * Try to regenerate a Cloudflare image URL with different parameters
 * This is used to retry loading an image that failed to load
 * 
 * @param cloudflareId The ID of the Cloudflare image
 * @param attempt The attempt number (used to modify parameters differently)
 * @returns A new URL to try loading the image from
 */
export function generateRetryUrl(cloudflareId: string, attempt: number): string {
  if (attempt === 1) {
    return `https://airheart.com/cdn-cgi/imagedelivery/1WwAU7RhPvfp2YzGsd1m0Q/${encodeURIComponent(cloudflareId)}/public?format=auto`
  }
  else if (attempt === 2) {
    return `https://airheart.com/cdn-cgi/imagedelivery/1WwAU7RhPvfp2YzGsd1m0Q/${encodeURIComponent(cloudflareId)}/public?quality=85`
  }
  return constructCdnUrl(cloudflareId)
}

/**
 * Creates an error handler for images that will retry loading the image
 * with different parameters before falling back to a placeholder
 * 
 * @param cloudflareId The Cloudflare image ID
 * @param context The image context (USER, LOCATION, or GENERAL)
 * @param maxRetries Maximum number of retry attempts
 * @param onComplete Optional callback when the retry process completes
 * @returns A function to handle the error event
 */
export function createImageErrorHandler(
  cloudflareId: string | null | undefined,
  context: ImageContext = 'GENERAL',
  maxRetries: number = 2,
  onComplete?: (success: boolean) => void
): (event: Event) => void {
  if (!cloudflareId) {
    return (event: Event) => {
      const imgElement = event.target as HTMLImageElement
      imgElement.src = DEFAULT_PLACEHOLDERS[context]
      onComplete?.(false)
    }
  }

  let retryCount = 0

  return function handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement
    
    if (retryCount < maxRetries) {
      retryCount++
      console.warn(`Image load error (attempt ${retryCount}):`, cloudflareId)
      imgElement.src = generateRetryUrl(cloudflareId, retryCount)
    } else {
      console.warn(`Image load failed after ${maxRetries} retries:`, cloudflareId)
      imgElement.src = DEFAULT_PLACEHOLDERS[context]
      onComplete?.(false)
    }
  }
}

/**
 * A simpler version that returns a preloaded image with retry logic
 * This is useful for components that preload images
 * 
 * @param cloudflareId The Cloudflare image ID
 * @param context The image context (USER, LOCATION, or GENERAL)
 * @param maxRetries Maximum number of retry attempts
 * @returns A promise that resolves with the loaded image URL or placeholder
 */
export function preloadImageWithRetry(
  cloudflareId: string | null | undefined,
  context: ImageContext = 'GENERAL',
  maxRetries: number = 2
): Promise<string> {
  return new Promise((resolve) => {
    if (!cloudflareId) {
      resolve(DEFAULT_PLACEHOLDERS[context])
      return
    }

    let retryCount = 0
    const img = new Image()

    img.onload = () => {
      resolve(img.src)
    }

    img.onerror = () => {
      if (retryCount < maxRetries) {
        retryCount++
        console.warn(`Image preload error (attempt ${retryCount}):`, cloudflareId)
        img.src = generateRetryUrl(cloudflareId, retryCount)
      } else {
        console.warn(`Image preload failed after ${maxRetries} retries:`, cloudflareId)
        resolve(DEFAULT_PLACEHOLDERS[context])
      }
    }

    img.src = constructCdnUrl(cloudflareId)
  })
}
